import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { scrollToError , getMedia} from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ISpec } from '../types/interfaces';
import { customStyles } from '../../../utils/SelectStyles';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';

const CreateSpecificationGroupForm = () => {
  const { id, slug } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['specs', id],
    invalidateQueryKey: ["specs"],
    id,
    singleGetApi: `/admin/specs/${id}`,
    addApi: '/admin/specs',
    editApi: `/admin/specs/${id}`,
    listRoute: `/specification-groups/specifications/${slug}`,
    itemName: t('specification'),
  });

  const validationSchema = Yup.object({
    icon: Yup.mixed(),
    name: Yup.object().shape({
      ar: Yup.string()
        .required(t('required'))
        // .matches(/^[\u0600-\u06FF\s]+$/, t('must_ar'))
        .min(3, t('name_short')),
      en: Yup.string()
        .required(t('required'))
        // .matches(/^[A-Za-z\s]+$/, t('must_en'))
        .min(3, t('name_short'))
    })
  });

  const initialValues: ISpec = {
    ...apiData,
    
    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    
    type: apiData?.type || 'text',
    icon: getMedia(apiData?.media, 'icon')?.url || '',
    specs_group_id: slug ? String(slug) : ''
  };


  const specTypesOptions = [
    { label: t('text'), value: 'text' },
    { label: t('checkbox'), value: 'checkbox' }
  ];

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 1, type: 'textarea' },
            { columns: 2, items: 2 },
            { columns: 1, items: 1 },
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value });
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('specification_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload errors={formik?.errors.icon as string} touched={formik?.touched?.icon as boolean} formik={formik} name={`icon`} label={t('icon')} />
                  </FieldWrapper>
                  <FieldWrapper noPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper
                    title={t('name_en')}
                    inputName={`name[en]`}
                    inputPlaceholder={t('name_en')}
                    inputError={formik.errors.name?.en as string}
                    inputTouched={formik.touched.name?.en as boolean}
                    input
                    customPadding
                  />
                  <FieldWrapper
                    title={t('name_ar')}
                    inputName={`name[ar]`}
                    inputPlaceholder={t('name_ar')}
                    inputError={formik.errors.name?.ar as string}
                    inputTouched={formik.touched.name?.ar as boolean}
                    input
                    customPadding
                  />
                </div>
                <div className="inputs_group">
                  <FieldWrapper customPadding title={t('type')} inputPlaceholder={t('type')} inputName={`type`} options={specTypesOptions} selectStyle={customStyles} select />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateSpecificationGroupForm;
